import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import { InView, useInView } from 'react-intersection-observer';
import styled, { css } from 'styled-components';

import { sortBy, uniq } from 'lodash';
import { useDispatch } from 'react-redux';
import TourGuide from 'src/components/Banners/TourGuide';
import HotelsLoadingPreview from 'src/components/Hotel/HotelsLoadingPreview';
import PenalizedHotels from 'src/routes/SearchPage/components/EmptyFiltration/components/PenalizedHotels';
import { COUNT_HOTELS_FOR_SHOW_PENALIZED } from 'src/routes/SearchPage/constants/hotels';
import {
  HOTEL_LIST_BANNER_SEARCH,
  HotelListBanner,
} from '../../../../components/HotelListBanner';
import HotelRenderer from '../../containers/HotelRenderer';

import type { HotelsProps } from './types';

import { setHotelsVisibility } from '../../store/hotels/slice';
import ShowMoreHotels from './components/ShowMoreHotels';

const Hotels = ({
  hotels,
  visibleLimit,
  penalizedHotelsVisibleLimit,
  fullMapView = false,
  isShownLoading,
  awaitFiltration,
  browser,
  isMobile,
  isEmptyFiltration,
  isVisiblePenalizedHotels,
  setCurrentVisibleHotels,
  setCurrentVisiblePenalizedHotels,
  hotelHovered,
  isWhitelabel = false,
  buildUrl,
  isHotelOnly,
  className,
  mobileHotelFilter,
}: HotelsProps) => {
  const dispatch = useDispatch();

  // стейт массива индексов отелей, находящихся в зоне видимости viewport
  const [visibleHotels, setVisibleHotels] = useState<number[]>([]);

  // стейт массива индексов пенализированных отелей, находящихся в зоне видимости viewport
  const [visiblePenalizedHotels, setVisiblePenalizedHotels] = useState<
    number[]
  >([]);

  const { ref, inView } = useInView({ triggerOnce: false });

  useEffect(() => {
    dispatch(setHotelsVisibility(inView));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const wrapperClass = classNames({
    'hotels-list': true,
    'hotels-list__small': fullMapView,
    'hotels-list-mobile': isMobile,
  });

  const isSmallCard = fullMapView || browser.lessThan.medium || isMobile;

  useEffect(() => {
    if (isMobile) {
      window.addEventListener('popstate', buildUrl);
    }
    return () => window.removeEventListener('popstate', buildUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isVisiblePenalizedHotels) setVisibleHotels([]);
    if (awaitFiltration) {
      setCurrentVisibleHotels(visibleHotels);
      setCurrentVisiblePenalizedHotels(visiblePenalizedHotels);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitFiltration, isVisiblePenalizedHotels]);

  const handleHotelVisibility = (index: number, isInView: boolean) => {
    if (!isVisiblePenalizedHotels) {
      let newVisibleHotels = [...visibleHotels];

      if (isInView) newVisibleHotels.push(index);
      else newVisibleHotels = newVisibleHotels.filter((n) => n !== index);

      // сортируем по возрастанию и убираем дубли
      setVisibleHotels(uniq(sortBy([...newVisibleHotels])));
    }
  };

  const handlePenalizedHotelVisibility = (index: number, isInView: boolean) => {
    let newVisiblePenalizedHotels = [...visiblePenalizedHotels];

    if (isInView) newVisiblePenalizedHotels.push(index);
    else
      newVisiblePenalizedHotels = newVisiblePenalizedHotels.filter(
        (n) => n !== index,
      );

    // сортируем по возрастанию и убираем дубли
    setVisiblePenalizedHotels(uniq(sortBy([...newVisiblePenalizedHotels])));
  };

  return (
    <main role="main" className={wrapperClass}>
      {isShownLoading ? (
        <HotelsLoadingPreview
          visibleLimit={visibleLimit}
          penalizedHotelsVisibleLimit={penalizedHotelsVisibleLimit}
          smallCard={Boolean(fullMapView || isMobile)}
        />
      ) : (
        <div ref={ref}>
          {hotels.slice(0, visibleLimit).map((hotel, index) => (
            <Wrapper
              key={hotel.tour_id}
              className={className}
              isMobile={isMobile}
            >
              {!mobileHotelFilter && (
                <StyledBanner
                  index={index}
                  type={HOTEL_LIST_BANNER_SEARCH}
                  isWhitelabel={isWhitelabel}
                />
              )}

              {index === 2 && !isHotelOnly && !mobileHotelFilter && (
                <StyledTourGuideBanner
                  isMobile={isMobile || isSmallCard}
                  isHotelOnly={isHotelOnly}
                />
              )}
              <InView
                onChange={(isInView) => handleHotelVisibility(index, isInView)}
              >
                <Hotel
                  index={index}
                  defaultImages={hotel.hotel.images}
                  hotelInfo={hotel}
                  isSmallCard={isSmallCard}
                  onHover={hotelHovered}
                />
              </InView>
            </Wrapper>
          ))}
        </div>
      )}

      <ShowMoreHotels />

      {!isEmptyFiltration &&
        hotels.length < COUNT_HOTELS_FOR_SHOW_PENALIZED &&
        !awaitFiltration &&
        !mobileHotelFilter && (
          <PenalizedHotels
            handlePenalizedHotelVisibility={handlePenalizedHotelVisibility}
          />
        )}
    </main>
  );
};

const marginBottomBetweenBlocks = css`
  margin-bottom: 8px;
`;

const StyledTourGuideBanner = styled(TourGuide)`
  ${marginBottomBetweenBlocks}
`;

const Wrapper = styled.div<{ isMobile: boolean }>`
  ${marginBottomBetweenBlocks}
  position: relative;
  ${({ isMobile }) => isMobile && 'padding: 0 8px'};
`;

const Hotel = styled(HotelRenderer)`
  max-width: 100vw;
  filter: drop-shadow(0px 1px 2px rgba(210, 208, 197, 0.9))
    drop-shadow(0px 10px 15px rgba(216, 214, 204, 0.9));
`;

const StyledBanner = styled(HotelListBanner)`
  ${marginBottomBetweenBlocks}
`;

export default Hotels;
